// Editing-side helpers for chatBot.agentUi: the agent's buttons, card options and context switches.
// Only super admins write agentUi, through this editor, so this validator owns the shape; the API
// re-checks only safety (link hosts, lengths, counts). null means nothing set: today's widget.

// Every language the widget has a language file for.
export const WIDGET_LOCALES = [
  'en',
  'es',
  'fr',
  'de',
  'it',
  'pl',
  'et',
  'ja',
  'ko',
  'th',
  'vi',
  'id',
  'zh',
  'ar',
  'bg',
  'ru',
];

// Registry functions the widget ships; any other name is a function the site registers.
export const BUILT_IN_FUNCTIONS = ['openLink', 'urlTemplate', 'ask', 'location', 'addToCart'];

export const MAX_ACTIONS = 10;
export const MAX_SAFE_URLS = 20;
export const LABEL_MAX = 40;
export const ASK_TEXT_MAX = 200;

const TYPE_PATTERN = /^[a-z][a-zA-Z0-9_]{1,30}$/;
const FUNCTION_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]{0,39}$/;
// A hostname, optionally starting with `*.`: dot-separated labels of letters, digits and inner dashes.
const SAFE_URL_PATTERN = /^(?:\*\.)?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export const blankAgentUi = () => ({
  safeUrls: [],
  actions: [],
  productCard: null,
  context: { today: false, page: false },
  location: { mode: 'off', ipCity: false },
});

// A stored agentUi may lack keys (the API checks safety, not shape); read it over the defaults.
const withDefaults = (agentUi) => ({ ...blankAgentUi(), ...agentUi });

export const blankAction = () => ({ type: '', do: 'openLink', labels: {}, onCards: false, inReplies: true });

const trimmed = (value) => (typeof value === 'string' ? value.trim() : '');

/** Trimmed types of the actions shown on cards, unique and non-empty: what a card button may name. */
export const cardActionTypes = (actions) =>
  [...new Set((actions || []).filter((action) => action.onCards).map((action) => trimmed(action.type)))].filter(
    Boolean
  );

/**
 * `agentUi` with `changes` merged in; a null agentUi starts from the defaults. Back to all off is
 * null again, so toggling a switch on and off leaves the bot as it was loaded.
 */
export const updateAgentUi = (agentUi, changes) => {
  const next = { ...withDefaults(agentUi), ...changes };
  const allOff =
    !next.actions.length &&
    !next.safeUrls.length &&
    !next.productCard &&
    !next.context.today &&
    !next.context.page &&
    next.location.mode !== 'ask' &&
    !next.location.ipCity;
  return allOff ? null : next;
};

/** Card options as stored: null when nothing is on, so the widget keeps today's cards. */
export const toProductCard = ({ price = false, fields = false, buttons = [], viewCartHref = '' } = {}) => {
  const href = viewCartHref.trim();
  if (!price && !fields && buttons.length === 0 && !href) return null;
  return { price, fields, buttons, ...(href && { viewCartHref: href }) };
};

/** A label or ask text map with `text` in `locale`; null removes the language. */
export const setLocaleText = (map, locale, text) => {
  const next = { ...map };
  if (text === null) delete next[locale];
  else next[locale] = { text };
  return next;
};

// Empty texts are dropped on save (the widget's own wording shows), so only filled ones are checked.
const textMapError = (map, max, at) => {
  for (const [locale, { text }] of Object.entries(map || {})) {
    if (!/^[a-z]{2}$/.test(locale)) return `${at} ${locale}: not a language code`;
    if (trimmed(text).length > max) return `${at} ${locale}: at most ${max} characters`;
  }
  return null;
};

const httpsError = (value, at) => {
  try {
    const url = new URL(value.trim());
    if (url.protocol !== 'https:') return `${at} must use https`;
    if (url.username || url.password) return `${at} must not carry a user or password`;
    return null;
  } catch {
    return `${at} must be an absolute https link`;
  }
};

// A template either continues a link the agent gives ({href}/…) or starts at a fixed https host.
const templateError = (template) => {
  if (template.startsWith('{href}'))
    return /^\{href\}(?:[/?#]|$)/.test(template) ? null : 'The template must continue after {href} with /, ? or #';
  if (!/^https:\/\/[^/?#{}]+(?:[/?#]|$)/.test(template))
    return 'The template must start with https:// and a fixed host, or with {href}';
  return null;
};

/** Per-field problems of one action: { type, do, href, template, askText, labels } (null = fine). */
export const actionErrors = (action, types = []) => {
  const type = trimmed(action.type);
  const fn = trimmed(action.do);
  const template = trimmed(action.template);

  let typeError = null;
  if (!TYPE_PATTERN.test(type)) typeError = 'A lowercase name: letters, digits, _ (2–31)';
  else if (types.filter((other) => other === type).length > 1) typeError = 'Another action uses this type';

  let templateErr = template ? templateError(template) : null;
  if (fn === 'urlTemplate' && !template) templateErr = 'A template is required for urlTemplate';

  let askError = textMapError(action.askText, ASK_TEXT_MAX, 'Ask text');
  if (!askError && fn === 'ask' && !Object.values(action.askText || {}).some(({ text }) => trimmed(text)))
    askError = 'Ask needs the text it sends in at least one language';

  return {
    type: typeError,
    do: FUNCTION_PATTERN.test(fn) ? null : 'Name a widget function',
    // No fixed link on openLink: the agent writes it in the tag.
    href: trimmed(action.href) ? httpsError(action.href, 'The link') : null,
    template: templateErr,
    askText: askError,
    labels: textMapError(action.labels, LABEL_MAX, 'Label'),
  };
};

/** Safe URL entries that are not hostnames (an optional leading `*.`). */
export const badSafeUrls = (safeUrls) => safeUrls.filter((host) => !SAFE_URL_PATTERN.test(host.trim().toLowerCase()));

/** First reason `agentUi` can't be saved, or null. A null agentUi is always fine. */
export const validateAgentUi = (agentUi) => {
  if (agentUi === null) return null;
  const { actions, safeUrls, productCard, location } = withDefaults(agentUi);
  if (actions.length > MAX_ACTIONS) return `Actions: at most ${MAX_ACTIONS}`;
  if (safeUrls.length > MAX_SAFE_URLS) return `Safe URLs: at most ${MAX_SAFE_URLS}`;
  const [badHost] = badSafeUrls(safeUrls);
  if (badHost !== undefined) return `Safe URLs: ${badHost} is not a hostname such as shop.example or *.shop.example`;

  const types = actions.map((action) => trimmed(action.type));
  for (const [index, action] of actions.entries()) {
    const message = Object.values(actionErrors(action, types)).find(Boolean);
    if (message) return `Action ${types[index] || index + 1}: ${message}`;
  }

  const cardTypes = cardActionTypes(actions);
  const button = (productCard?.buttons ?? []).find((type) => !cardTypes.includes(type));
  if (button !== undefined) return `Card buttons: ${button} is not an action shown on cards`;
  if (productCard?.viewCartHref) {
    const error = httpsError(productCard.viewCartHref, 'Card options: the cart link');
    if (error) return error;
  }
  const locationAction = actions.some(
    (action) => trimmed(action.type) === 'location' || trimmed(action.do) === 'location'
  );
  if (locationAction && location.mode !== 'ask') return 'Actions: a location action needs Shopper location set to Ask';
  return null;
};

const cleanTextMap = (map) =>
  Object.fromEntries(
    Object.entries(map || {})
      .filter(([, { text }]) => trimmed(text))
      .map(([locale, { text }]) => [locale, { text: text.trim() }])
  );

const cleanAction = (action) => ({
  type: action.type.trim(),
  do: action.do.trim(),
  labels: cleanTextMap(action.labels),
  ...(trimmed(action.href) && { href: action.href.trim() }),
  ...(trimmed(action.template) && { template: action.template.trim() }),
  ...(action.askText && Object.keys(action.askText).length && { askText: cleanTextMap(action.askText) }),
  ...(action.params && { params: action.params }),
  onCards: action.onCards === true,
  inReplies: action.inReplies === true,
});

/** agentUi as the API takes it: exactly its keys, texts and hosts trimmed. null stays null. */
export const toAgentUiPayload = (agentUi) => {
  if (agentUi === null) return null;
  const { safeUrls, actions, productCard, context, location } = withDefaults(agentUi);
  return {
    safeUrls: [...new Set(safeUrls.map((host) => host.trim().toLowerCase()))],
    actions: actions.map(cleanAction),
    productCard: productCard && toProductCard(productCard),
    context: { today: context.today === true, page: context.page === true },
    location: { mode: location.mode === 'ask' ? 'ask' : 'off', ipCity: location.ipCity === true },
  };
};

/**
 * Actions an initial message can carry as `<action type="…" />`: only those that run without tag
 * parameters — a fixed link, ask, location, or a site function (its fixed params come from settings).
 */
export const initialMessageActions = (agentUi) =>
  (agentUi?.actions ?? []).filter((action, index, all) => {
    if (!TYPE_PATTERN.test(trimmed(action.type))) return false;
    // Unsaved edits can repeat a type; the option list must not (Mantine throws on duplicates).
    if (all.findIndex((other) => trimmed(other.type) === trimmed(action.type)) !== index) return false;
    if (action.do === 'openLink') return Boolean(trimmed(action.href));
    if (action.do === 'ask' || action.do === 'location') return true;
    return !BUILT_IN_FUNCTIONS.includes(action.do);
  });
